import React from 'react';
import Appearance from 'styles/Appearance.js';
import ProductCard from 'views/ProductCard.js';
import Button from 'views/Button.js';
import Utils from 'files/Utils.js';
import { useServiceability } from 'views/ServiceabilityProvider.js';
import { offerPercent, offerPricing } from 'files/LimitedOffer.js';
import OfferTag from 'views/OfferTag.js';
import PriceGate from 'views/PriceGate.js';
import ShopPayInstallments from 'views/ShopPayInstallments.js';

const ProductGrid = ({ products, onAddToCart, onViewDetails, loading, onOpenQuiz }) => {

    const { canBuyOnline, offer } = useServiceability();

    const all = products || [];
    const bundle = all.find(p => p.bundle);              // Base Network — the hero
    const alaCarte = all.filter(p => !p.bundle);         // individual devices — secondary

    // Same numbers the price row shows, so "Save $X" always matches the
    // struck-through price (compare-at and/or a live limited-time offer).
    const bundlePricing = bundle ? offerPricing(bundle, offer) : null;
    const savings = bundlePricing && bundlePricing.was
        ? Math.max(0, parseFloat(bundlePricing.was) - bundlePricing.price)
        : 0;

    // Skeleton card for the à-la-carte grid while live data loads.
    const skeletonCard = (i) => (
        <div key={i} style={{ ...Appearance.styles.card(), width: '100%', maxWidth: 360, boxShadow: Appearance.boxShadow.card }}>
            <div className={'skeleton'} style={{ height: 260, borderRadius: 0 }} />
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className={'skeleton'} style={{ height: 12, width: '40%' }} />
                <div className={'skeleton'} style={{ height: 22, width: '70%' }} />
                <div className={'skeleton'} style={{ height: 14, width: '100%' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                    <div className={'skeleton'} style={{ height: 22, width: 64 }} />
                    <div className={'skeleton'} style={{ height: 38, width: 110, borderRadius: 19 }} />
                </div>
            </div>
        </div>
    );

    // --- Base Network hero -----------------------------------------------
    // Desktop: photo left, details right, short enough that price and Add to
    // Cart sit beside the photo. The photo is ~1.84:1 with the devices between
    // ~22% and ~80% of its width, so filling a ~1.5:1 column only trims empty
    // background at the edges.
    const mobile = Utils.isMobile();
    // Side by side needs ~960px; narrower (phones, upright tablets) stacks, or
    // the photo column turns tall and narrow and crops the devices.
    const stackedBundle = mobile || document.documentElement.clientWidth < 960;
    const bundleOffer = offerPercent(bundle, offer);
    const bundleIncludes = bundle && (bundle.includes || (bundle.features || []).slice(0, 3));

    const starterHero = bundle && (
        <div style={{
            ...Appearance.styles.card(),
            boxShadow: Appearance.boxShadow.card,
            overflow: 'hidden',
            marginBottom: 64,
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr',
        }}>
            <div style={{ position: 'relative', backgroundColor: Appearance.colors.offWhite, minHeight: 340, overflow: 'hidden' }}>
                {bundle.images && bundle.images.length > 0 && (
                    <img src={bundle.images[0].src} alt={bundle.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 50%' }} />
                )}
                {canBuyOnline && (
                    <OfferTag compact percent={bundleOffer} style={{ position: 'absolute', top: 16, left: 16 }} />
                )}
            </div>

            <div style={{ padding: '36px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <span style={{ ...Appearance.textStyles.label({ color: Appearance.colors.primary() }), marginBottom: 10 }}>
                    {bundle.subtitle || 'Complete system'}
                </span>
                <h3 style={{ ...Appearance.textStyles.heading({ fontSize: 28 }), marginBottom: 14, textWrap: 'balance' }}>
                    {bundle.title}
                </h3>

                {bundleIncludes && bundleIncludes.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 22 }}>
                        {bundleIncludes.map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <i className={'fas fa-check'} style={{ fontSize: 12, color: Appearance.colors.primary() }} />
                                <span style={Appearance.textStyles.body({ fontSize: 15, color: Appearance.colors.text() })}>{item}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Price → installments → Add to Cart — gated by serviceability */}
                <PriceGate buttonStyle={{ padding: '14px 28px', fontSize: 15 }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 2 }}>
                            <span style={Appearance.textStyles.heading({ fontSize: 32 })}>
                                {Utils.formatCurrency(bundlePricing.price)}
                            </span>
                            {bundlePricing.was && (
                                <span style={{ ...Appearance.textStyles.body({ fontSize: 16 }), textDecoration: 'line-through', color: Appearance.colors.lightGrey }}>
                                    {Utils.formatCurrency(bundlePricing.was)}
                                </span>
                            )}
                            {/* No offer running: show a plain compare-at saving instead */}
                            {!bundleOffer && savings > 0 && (
                                <span style={Appearance.textStyles.caption({ fontWeight: 600, color: Appearance.colors.success })}>
                                    {`Save ${Utils.formatCurrency(savings)}`}
                                </span>
                            )}
                        </div>
                        <ShopPayInstallments price={bundlePricing.price} />
                        <div style={{ marginTop: 18 }}>
                            <Button label={'Add to Cart'} type={'primary'} onClick={() => onAddToCart(bundle, 'product_grid_bundle')} style={{ width: '100%', padding: '14px 28px', fontSize: 15 }} />
                        </div>
                    </div>
                </PriceGate>

                {/* The quiz stays available to everyone as the secondary path */}
                <span
                    onClick={onOpenQuiz}
                    style={{
                        display: 'block',
                        marginTop: 14,
                        textAlign: 'center',
                        color: Appearance.colors.primary(),
                        fontFamily: Appearance.fonts.body,
                        fontSize: 15,
                        fontWeight: 500,
                        cursor: 'pointer',
                    }}>
                    {'Not sure? Find Your Solution\u00A0›'}
                </span>
            </div>
        </div>
    );

    // Phones: fits on one screen — shorter banner (no badge; the struck price
    // carries the discount), one-line name, short "includes" lines instead of
    // the label, sentence and long feature lines.
    const starterHeroMobile = bundle && (
        <div style={{
            ...Appearance.styles.card(),
            boxShadow: Appearance.boxShadow.card,
            overflow: 'hidden',
            marginBottom: 40,
            ...(mobile ? {} : { maxWidth: 600, marginLeft: 'auto', marginRight: 'auto', marginBottom: 56 }),
        }}>
            <div style={{ backgroundColor: Appearance.colors.offWhite, aspectRatio: '2 / 1', overflow: 'hidden' }}>
                {bundle.images && bundle.images.length > 0 && (
                    <img src={bundle.images[0].src} alt={bundle.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                )}
            </div>

            <div style={{ padding: '18px 20px 22px' }}>
                <h3 style={{ ...Appearance.textStyles.heading({ fontSize: 21 }), marginBottom: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {bundle.title}
                </h3>

                {(bundle.includes || bundle.features) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                        {(bundle.includes || bundle.features.slice(0, 3)).map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                <i className={'fas fa-check'} style={{ fontSize: 11, color: Appearance.colors.primary() }} />
                                <span style={Appearance.textStyles.body({ fontSize: 14.5, color: Appearance.colors.text() })}>{item}</span>
                            </div>
                        ))}
                    </div>
                )}

                <PriceGate buttonStyle={{ padding: '13px 24px', fontSize: 15 }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
                            <span style={Appearance.textStyles.heading({ fontSize: 28 })}>
                                {Utils.formatCurrency(bundlePricing.price)}
                            </span>
                            {bundlePricing.was && (
                                <span style={{ ...Appearance.textStyles.body({ fontSize: 15 }), textDecoration: 'line-through', color: Appearance.colors.lightGrey }}>
                                    {Utils.formatCurrency(bundlePricing.was)}
                                </span>
                            )}
                        </div>
                        <ShopPayInstallments price={bundlePricing.price} />
                        <div style={{ marginTop: 14 }}>
                            <Button label={'Add to Cart'} type={'primary'} onClick={() => onAddToCart(bundle, 'product_grid_bundle')} style={{ width: '100%', padding: '13px 24px', fontSize: 15 }} />
                        </div>
                    </div>
                </PriceGate>

                <span
                    onClick={onOpenQuiz}
                    style={{
                        display: 'block',
                        marginTop: 12,
                        textAlign: 'center',
                        color: Appearance.colors.primary(),
                        fontFamily: Appearance.fonts.body,
                        fontSize: 14.5,
                        fontWeight: 500,
                        cursor: 'pointer',
                    }}>
                    {'Not sure? Find Your Solution\u00A0›'}
                </span>
            </div>
        </div>
    );

    return (
        <section id={'products'} style={{ width: '100%', backgroundColor: Appearance.colors.white }}>
            <div style={{ maxWidth: Appearance.layout.maxWidth, margin: '0 auto', padding: Appearance.layout.sectionPadding() }}>

                {/* Section Header */}
                <div style={{ textAlign: 'center', marginBottom: Utils.isMobile() ? 36 : 56 }}>
                    <span style={{ ...Appearance.textStyles.label(), display: 'block', marginBottom: 14 }}>
                        {'Shop'}
                    </span>
                    <h2 style={Appearance.textStyles.sectionHeading()}>{'Choose Your Protection'}</h2>
                    <p style={{ ...Appearance.textStyles.sectionSubheading(), margin: '16px auto 0' }}>
                        {'Begin with the Base Network, or answer a couple of quick questions and we’ll size a system to your home.'}
                    </p>
                </div>

                {loading ? (
                    <div style={{ display: 'grid', gridTemplateColumns: Utils.isMobile() ? '1fr' : 'repeat(3, 1fr)', gap: 30, justifyItems: 'center' }}>
                        {[0, 1, 2].map(i => skeletonCard(i))}
                    </div>
                ) : (
                    <>
                        {/* Hero — Base Network */}
                        {stackedBundle ? starterHeroMobile : starterHero}

                        {/* Secondary — à-la-carte devices */}
                        {alaCarte.length > 0 && (
                            <>
                                <div style={{ textAlign: 'center', marginBottom: Utils.isMobile() ? 28 : 40 }}>
                                    <h3 style={{ ...Appearance.textStyles.heading({ fontSize: Utils.isMobile() ? 22 : 26 }), marginBottom: 8 }}>
                                        {'Or add individual devices'}
                                    </h3>
                                    <p style={Appearance.textStyles.sectionSubheading({ margin: '0 auto' })}>
                                        {'Expand your system any time — buy sensors and devices à la carte.'}
                                    </p>
                                </div>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: Utils.isMobile() ? '1fr' : `repeat(${Math.min(alaCarte.length, 3)}, 1fr)`,
                                    gap: Utils.isMobile() ? 12 : 30,
                                    justifyItems: 'center',
                                }}>
                                    {alaCarte.map((product, index) => (
                                        <ProductCard
                                            key={product.id || index}
                                            product={product}
                                            onAddToCart={() => onAddToCart(product, 'product_grid')}
                                            onViewDetails={() => onViewDetails(product)} />
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </section>
    );
};

export default ProductGrid;
